import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import ArrowSquareUpIcon from "@icons/arrow-square-up.svg?react";
import classNames from "classnames";
import { downloadFromStorage, uploadToStorage } from "@/common/lib/supabaseClient";
import FileItem from "./FileItem";
import { downloadBlob } from "@/features/patient-view/components/documents/editor/utils";
import { IMPORT_APPOINTMENTS_SAMPLE, PUBLIC_STORAGE_BUCKET } from "@/common/utils/constants";
import { prepareCsvFromFile } from "./ImportModal";
import Papa from "papaparse";

// Dropzone styles
export const baseStyle = {
    flex: 1,
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderRadius: 12,
    borderColor: "#98A2B3",
    borderStyle: "dashed",
    backgroundColor: "#fff",
    color: "#b7b7b8",
    outline: "none",
    transition: "border .24s ease-in-out",
};

export const focusedStyle = {
    borderColor: "#1a7c7f",
};

export const acceptStyle = {
    borderColor: "#1a7c7f",
};

export const rejectStyle = {
    borderColor: "#F04438",
};

export const hoveredStyle = {
    backgroundColor: "#fff",
    borderColor: "#1a7c7f",
    cursor: "pointer",
};

interface Props {
    bucket: string;
    relativePath: string;
    fileType: { [key: string]: readonly string[] };
    file: File | null;
    setFile: React.Dispatch<React.SetStateAction<File | null>>;
    removeFile: (file: File) => void;
}

const DragNDrop: React.FC<Props> = ({ bucket, relativePath, fileType, file, setFile, removeFile }) => {
    const [loading, setLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Handle file drop event and upload to storage
     */
    const onDrop = useCallback(
        (file: File[]) => {
            if (error) {
                setError(null);
            }

            if (file) {
                setFile(file[0]);
                handleUpload(file[0]);
            }
        },
        [error],
    );

    /**
     * Handle file drop rejected event and set error
     */
    const onDropRejected = useCallback((fileRejections: any) => {
        setError(fileRejections[0].errors[0].message);
    }, []);

    /**
     * Handle error event and set error
     */
    const onError = useCallback((err: any) => {
        setError(err.message);
    }, []);

    /**
     * Dropzone hook to handle events https://react-dropzone.js.org/#src
     */
    const { getRootProps, isFocused, isDragAccept, isDragReject, getInputProps } = useDropzone({
        onDrop,
        onDropRejected,
        onError,
        accept: fileType,
    });

    /**
     * Set styles based on dropzone state
     */
    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
            ...(isHovered ? hoveredStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject, isHovered],
    );

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    /**
     * Upload file to storage
     * @param file
     */
    const handleUpload = async (file: File) => {
        if (error) {
            setError(null);
        }

        if (!file) {
            setError("File not found");
            return;
        }

        setLoading(true);

        const parsed = await prepareCsvFromFile(file);

        if (!parsed?.data || !parsed?.meta?.fields) {
            setError("Failed to parse CSV");
            setLoading(false);
            return;
        }

        // turn back into csv file including headers
        const csvString = Papa.unparse({ fields: parsed.meta.fields, data: parsed.data }, { header: true });

        // create a new Blob with the CSV string
        const csvBlob = new Blob([csvString], { type: "text/csv" });

        // create a new File object with the Blob
        const csvFile = new File([csvBlob], file.name, { type: "text/csv" });

        // upload the file to storage
        const { error: uploadError } = await uploadToStorage(bucket, relativePath, file.name, csvFile);

        if (uploadError) {
            setError(uploadError.message);
            setLoading(false);
        }

        setLoading(false);
    };

    /**
     * Download file from storage
     * @param bucket
     * @param path
     * @returns
     */
    const handleDownload = async (bucket: string, path: string) => {
        const { data, error } = await downloadFromStorage(bucket, path);

        if (error) {
            setError(error.message);
            return;
        }

        if (data) {
            downloadBlob(data, path);
            return;
        }
    };

    /**
     * Render dropzone component
     */
    const renderDropzone = () => {
        return (
            <div
                id={"dropzoneTime"}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                {...getRootProps({ style })}
                className="dropzone"
            >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center justify-center gap-2">
                    <div className="p-2 bg-white rounded-md ring-1 ring-gray-200">
                        <ArrowSquareUpIcon className="h-5 w-5 text-gray-700" />
                    </div>
                    <div className="text-gray-600 text-xs">
                        <span className="text-max-900">Click to upload</span> or drag and drop
                    </div>
                    <span className="text-gray-600 text-xs">.csv file with up to 5,000 rows works best</span>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4">
            <div className="w-full px-6">
                {/* if a file has been added render that file */}
                {file ? <FileItem file={file} onRemove={removeFile} loading={loading} /> : renderDropzone()}
            </div>

            <div className="text-center text-gray-500 text-sm px-6">
                <a
                    className="underline"
                    href="https://help.gohappydoc.com/en/articles/10408233-uploading-appointments"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Learn more
                </a>{" "}
                about importing a CSV or{" "}
                <span
                    role="button"
                    tabIndex={0}
                    className="underline cursor-pointer"
                    onClick={() => handleDownload(PUBLIC_STORAGE_BUCKET, IMPORT_APPOINTMENTS_SAMPLE)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            handleDownload(PUBLIC_STORAGE_BUCKET, IMPORT_APPOINTMENTS_SAMPLE);
                        }
                    }}
                >
                    download a sample CSV file
                </span>
            </div>
            {/* show error state */}
            {error && (
                <div
                    className={classNames(
                        "flex w-full py-3 mt-2 px-2 items-center justify-center gap-4 bg-red-50",
                        "border-l-[6px] border-t-[1px] border-b-[1px] border-r-0 border-red-600",
                    )}
                >
                    <span className={classNames("text-xs font-medium ", "text-red-900")}>{error}</span>
                    {file && (
                        <span
                            role="button"
                            tabIndex={0}
                            className={classNames("text-xs font-semibold underline cursor-pointer", "text-red-900")}
                            onClick={() => handleUpload(file)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    handleUpload(file);
                                }
                            }}
                        >
                            Retry
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default DragNDrop;
